/**
 * Owner: Masa Bayu
 * File: sample-access-store.ts
 * Date:Time: 2026-10-17 16:20
 * Description: Read Sample access per buku — penulis/penerbit tetapkan had halaman
 *   sampel (contoh: baca hingga halaman 50). Persist localStorage; lalai demo:
 *   setiap buku katalog dibuka sampel 50 halaman sehingga penulis menetapkan lain.
 *   §509 F1 — apabila hub mendeklarasi `sampleMaxPage`, deklarasi itu jadi bumbung
 *   yang mengikat; lapisan localStorage hanya boleh menurunkan, bukan menaikkan.
 */

import { listCatalog } from '../publication/catalog-port';
import { getHubSampleCeiling } from '../publication/hub-sample-ceiling';

export interface SampleAccessSetting {
  readonly enabled: boolean;
  readonly maxPage: number;
}

export interface SampleAccessRow extends SampleAccessSetting {
  readonly publicationId: string;
  readonly title: string;
}

export const SAMPLE_ACCESS_STORAGE_KEY = 'qxread.sample-access.v1';
export const SAMPLE_ACCESS_DEFAULT_MAX_PAGE = 50;
export const SAMPLE_ACCESS_MIN_PAGE = 1;
export const SAMPLE_ACCESS_MAX_PAGE_CAP = 500;

type SampleAccessOverrides = Record<string, SampleAccessSetting>;

function defaultSetting(): SampleAccessSetting {
  return { enabled: true, maxPage: SAMPLE_ACCESS_DEFAULT_MAX_PAGE };
}

function clampMaxPage(value: number): number {
  if (!Number.isFinite(value)) {
    return SAMPLE_ACCESS_DEFAULT_MAX_PAGE;
  }
  return Math.min(
    SAMPLE_ACCESS_MAX_PAGE_CAP,
    Math.max(SAMPLE_ACCESS_MIN_PAGE, Math.round(value)),
  );
}

function readOverrides(): SampleAccessOverrides {
  try {
    const raw = window.localStorage.getItem(SAMPLE_ACCESS_STORAGE_KEY);
    if (raw === null) {
      return {};
    }
    const parsed: unknown = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) {
      return {};
    }
    const overrides: SampleAccessOverrides = {};
    for (const [key, value] of Object.entries(parsed)) {
      if (typeof value !== 'object' || value === null) {
        continue;
      }
      const record = value as Record<string, unknown>;
      if (typeof record.enabled !== 'boolean' || typeof record.maxPage !== 'number') {
        continue;
      }
      overrides[key] = {
        enabled: record.enabled,
        maxPage: clampMaxPage(record.maxPage),
      };
    }
    return overrides;
  } catch {
    return {};
  }
}

function writeOverrides(overrides: SampleAccessOverrides): void {
  try {
    window.localStorage.setItem(SAMPLE_ACCESS_STORAGE_KEY, JSON.stringify(overrides));
  } catch {
    // Storage penuh/ditolak — mod demo kekal dalam memori sesi ini.
  }
}

/**
 * Tetapan sampel satu buku.
 *
 * §509 F1 — autoriti had sampel ialah penerbit (descriptor hub `sampleMaxPage`),
 * BUKAN pelawat. Nilai hub ialah bumbung yang mengikat; lapisan setempat hanya boleh
 * MENURUNKAN (penulis menutup atau memotong), tidak boleh exceeded deklarasi server.
 * Tanpa deklarasi hub (id terikat-kod / luar talian) → lapisan setempat seperti sedia kala.
 * §510-S2 — bumbung dibaca dari `hub-sample-ceiling` (setiap descriptor katalog),
 * bukan dari peta harga; buku tanpa harga dahulu tiada bumbung dan pelawat boleh
 * membaca 50 halaman. §510-S1 — bumbung 0 dihormati sebagai 0 (sampel ditutup),
 * bukan di-clamp ke 1.
 */
export function getSampleAccess(publicationId: string): SampleAccessSetting {
  const trimmed = publicationId.trim();
  if (trimmed.length === 0) {
    return { enabled: false, maxPage: SAMPLE_ACCESS_DEFAULT_MAX_PAGE };
  }
  const local = readOverrides()[trimmed] ?? defaultSetting();
  const declared = getHubSampleCeiling(trimmed);
  if (declared === null) {
    return local;
  }
  if (declared <= 0) {
    return { enabled: false, maxPage: 0 };
  }
  return { enabled: local.enabled, maxPage: Math.min(local.maxPage, declared) };
}

/** Tulis tetapan sampel satu buku (merge ke atas lalai) + persist. */
export function setSampleAccess(
  publicationId: string,
  patch: Partial<SampleAccessSetting>,
): SampleAccessSetting {
  const trimmed = publicationId.trim();
  if (trimmed.length === 0) {
    return { enabled: false, maxPage: SAMPLE_ACCESS_DEFAULT_MAX_PAGE };
  }
  const overrides = readOverrides();
  const current = overrides[trimmed] ?? defaultSetting();
  const next: SampleAccessSetting = {
    enabled: patch.enabled ?? current.enabled,
    maxPage: patch.maxPage !== undefined ? clampMaxPage(patch.maxPage) : current.maxPage,
  };
  overrides[trimmed] = next;
  writeOverrides(overrides);
  return next;
}

/** Senarai tetapan sampel untuk semua buku katalog (untuk skrin Author publications). */
export function listSampleAccess(): readonly SampleAccessRow[] {
  const overrides = readOverrides();
  return listCatalog().map((entry) => {
    const setting = overrides[entry.publicationId] ?? defaultSetting();
    return {
      publicationId: entry.publicationId,
      title: entry.title,
      enabled: setting.enabled,
      maxPage: setting.maxPage,
    };
  });
}
